import { type RefObject, useEffect, useState } from 'react';

import { getNearestScrollParent } from '@/utils/get-nearest-scroll-parent';

/**
 * Scroll offset used by sticky headers for shadow / scale effects.
 * Uses `window` on real pages; in Studio preview, attaches to the nearest scroll parent.
 */
export function useScrollYForHeaderLayout(
  isStudioPreview: boolean | undefined,
  stickyRef: RefObject<HTMLElement | null>,
): number {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!isStudioPreview) {
      const handleScroll = () => setScrollY(window.scrollY);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }

    let cancelled = false;
    let scrollParent: HTMLElement | null = null;
    let raf = 0;
    let tries = 0;

    const onScroll = () => {
      if (scrollParent) setScrollY(scrollParent.scrollTop);
    };

    const tryAttach = () => {
      if (cancelled || tries++ > 120) return;
      if (!stickyRef.current) {
        raf = requestAnimationFrame(tryAttach);
        return;
      }
      scrollParent = getNearestScrollParent(stickyRef.current);
      if (!scrollParent) {
        raf = requestAnimationFrame(tryAttach);
        return;
      }
      scrollParent.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    };

    raf = requestAnimationFrame(tryAttach);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      scrollParent?.removeEventListener('scroll', onScroll);
    };
  }, [isStudioPreview, stickyRef]);

  return scrollY;
}
